import { IonButton, IonFabButton, IonIcon, IonSearchbar, IonSelect, IonSelectOption } from '@ionic/react';
import { arrowDownOutline, arrowUpOutline } from 'ionicons/icons';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { setSearchParams } from '../../config/utils/urlsUtils';
import { SORT_DIRECTION } from '../../interfaces/enumerables';
import { SortBy, ValueLabel } from '../../interfaces/forms.interface';
import { PATHS } from '../../navigation/data';
import useDebounce from '../../services/hooks/useDebounce';

interface Props {
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
  sortDirection: SORT_DIRECTION;
  setSortDirection: (sortDirection: SORT_DIRECTION) => void;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  sortByFilterData: { [key: string]: SortBy };
  hideCreateButton?: boolean;
  hideSearchButton?: boolean;
  type: ValueLabel | undefined;
  setType: (sortBy: ValueLabel) => void;
  typeFilterData: Record<string, ValueLabel> | undefined;
}

export const Filters = ({
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  search,
  setSearch,
  sortByFilterData,
  hideCreateButton,
  hideSearchButton,
  type,
  setType,
  typeFilterData,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [localSearch, setLocalSearch] = useState(search);
  const debouncedValue = useDebounce(localSearch, 300);

  // Helper to update query params
  const updateQueryParams = useCallback(
    (params: { [key: string]: string | null }) => {
      const searchParams = setSearchParams(params, location.search);
      navigate({ search: searchParams.toString() }, { replace: true });
    },
    [location.search, navigate],
  );

  useEffect(() => {
    if (search !== debouncedValue) {
      setSearch(debouncedValue);
      updateQueryParams({ search: debouncedValue, page: '1' });
    }
  }, [localSearch, debouncedValue, setSearch, updateQueryParams, search]);

  const addNew = useCallback(() => {
    navigate(`${location.pathname}/${PATHS.CREATE_SUB_PATH}`);
  }, [location.pathname, navigate]);

  const onSortByChange = useCallback(
    (event: any) => {
      const newSortBy = sortByFilterData[event.detail.value];
      setSortBy(newSortBy);
      updateQueryParams({ sortBy: newSortBy.value });
    },
    [setSortBy, sortByFilterData, updateQueryParams],
  );

  const onTypeChange = useCallback(
    (event: any) => {
      const newType = typeFilterData?.[event.detail.value];
      if (newType) {
        setType(newType);
        updateQueryParams({ type: newType.value });
      }
    },
    [setType, typeFilterData, updateQueryParams],
  );

  const toggleSortDirection = useCallback(() => {
    const newSortDirection = sortDirection === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC;
    setSortDirection(newSortDirection);
    updateQueryParams({ sortDirection: newSortDirection });
  }, [setSortDirection, sortDirection, updateQueryParams]);

  const getSortIcon = useCallback(() => {
    if (sortDirection === SORT_DIRECTION.DESC) return arrowDownOutline;

    return arrowUpOutline;
  }, [sortDirection]);

  const handleSearchChange = useCallback(
    (ev: any) => {
      const value = ev?.detail?.value || '';
      setLocalSearch(value);
    },
    [setLocalSearch],
  );

  const getSelectOptions = useCallback(() => {
    return Object.keys(sortByFilterData).map((sortByKey: any) => {
      const val = sortByFilterData[sortByKey].value;
      return (
        <IonSelectOption key={val} value={val} className="ion-select-option-16">
          {sortByFilterData[sortByKey].label}
        </IonSelectOption>
      );
    });
  }, [sortByFilterData]);

  const getTypeOptions = useCallback(() => {
    if (typeFilterData) {
      return Object.keys(typeFilterData).map((sortByKey: any) => {
        const val = typeFilterData[sortByKey].value;
        return (
          <IonSelectOption key={val} value={val} className="ion-select-option-16">
            {typeFilterData[sortByKey].label}
          </IonSelectOption>
        );
      });
    }
  }, [typeFilterData]);

  const getTypeSelect = useCallback(() => {
    if (typeFilterData) {
      return (
        <div className="margin-r-6 margin-t-16">
          <IonSelect
            mode="ios"
            interface="action-sheet"
            value={type?.value}
            className="text-center ion-filter-button"
            style={{ maxWidth: 200 }}
            placeholder={t('FILTERS.SORT_BY')}
            cancelText={t('SHARED.CANCEL')}
            onIonChange={onTypeChange}>
            {getTypeOptions()}
          </IonSelect>
        </div>
      );
    }
  }, [getTypeOptions, onTypeChange, t, type?.value, typeFilterData]);

  const getCreateButton = useCallback(() => {
    if (hideCreateButton) {
      return null;
    }

    return (
      <div className="margin-r-6 margin-t-16">
        <IonButton mode="md" onClick={addNew} style={{ width: 100 }} className="no-padding no-margin">
          {t('SHARED.ADD')}
        </IonButton>
      </div>
    );
  }, [addNew, hideCreateButton, t]);

  const getSearchInput = useCallback(() => {
    if (hideSearchButton) return null;

    return (
      <IonSearchbar
        mode="md"
        className="g-search"
        value={localSearch}
        placeholder={t('FILTERS.SEARCH')}
        onIonInput={handleSearchChange}
      />
    );
  }, [hideSearchButton, localSearch, t, handleSearchChange]);

  return (
    <div className="flex-space-between-center-row-wrap margin-b-10">
      <div className="flex-start-center-row-wrap">
        {getCreateButton()}
        {getTypeSelect()}
        <div className="margin-r-6 margin-t-16">
          <IonSelect
            mode="ios"
            interface="action-sheet"
            value={sortBy.value}
            className="text-center ion-filter-button"
            placeholder={t('FILTERS.SORT_BY')}
            cancelText={t('SHARED.CANCEL')}
            style={{ maxWidth: 200 }}
            onIonChange={onSortByChange}>
            {getSelectOptions()}
          </IonSelect>
        </div>
        <div className="margin-t-16">
          <IonFabButton size="small" onClick={toggleSortDirection} className="ion-sort-button">
            <IonIcon icon={getSortIcon()} size="small" />
          </IonFabButton>
        </div>
      </div>
      <div className="margin-t-16">{getSearchInput()}</div>
    </div>
  );
};
